import { useEffect } from "react";
import { useLocation } from "react-router";

import { POST_AUTHENTICATED_USER } from "~/app/main/auth-config.ts";
import { NotFoundContent } from "~/features/not-found/not-found-content.tsx";
import { AuthRedirect } from "~/pages/auth/auth-redirect.tsx";
import { B2CLoginRedirect } from "~/shared/utils/b2c-login-redirect.tsx";
import { isValidRoute } from "~/shared/utils/is-valid-external-route.ts";

type DeepLinkValidatorProps = {
	readonly isExternal: boolean;
};

/**
 * Validates a deep-linked URL path against known application routes.
 * For external users, valid routes trigger a B2C sign-in redirect (with URL preservation).
 * For internal users, valid routes trigger an Azure AD sign-in redirect (with URL preservation).
 * Invalid routes always show a 404, regardless of user type.
 */
export function DeepLinkValidator({ isExternal }: DeepLinkValidatorProps) {
	const location = useLocation();
	// For internal users, allow all routes (including internal-only routes).
	// For external users, only allow external-accessible routes.
	const isValid = isValidRoute(location.pathname, {
		includeInternal: !isExternal,
	});

	useEffect(() => {
		// For both user types with valid routes, persist the path for post-auth navigation
		if (isValid) {
			const pathWithSearch = location.pathname + location.search;
			globalThis.sessionStorage.setItem(
				POST_AUTHENTICATED_USER,
				pathWithSearch,
			);
		}
	}, [location, isValid]);

	// Invalid route: show 404 for both internal and external users
	if (!isValid) {
		return <NotFoundContent />;
	}

	// Valid route for external user: redirect to B2C sign-in
	if (isExternal) {
		return <B2CLoginRedirect />;
	}

	// Valid route for internal user: redirect to Azure AD sign-in
	return <AuthRedirect />;
}
